import React, { ChangeEvent, useMemo, useState } from 'react';
import styled from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Input from 'components/UI/Input';
import Button from 'components/UI/Button';
import Icon from 'view/Icon';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import { lookupCatalogItemBySku } from 'requests/product';
import CatalogLookupResult, { CatalogLookupResponse } from 'data/CatalogLookupResult';
import { Main, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { TEXT_LABELS } from 'constants/order';
import { stripHtmlTags } from 'utils/formatters';
import {
  SectionCard,
  SectionTitle,
  HelperText,
  TableWrapper,
  Table,
  HeaderCell,
  BodyCell,
  ProductImage,
  ProductName,
  ProductDescription,
  SkuText,
  ActionCellContent
} from './EditOrder.styles';

const SearchResultsWrapper = styled.div`
  margin-top: ${Space.sp2x};
`;

const FieldLabel = styled.span`
  color: ${Text.primary};
  font-size: 14px;
  line-height: 20px;
`;

const Asterisk = styled.span`
  color: ${Main.error};
  margin-left: ${Space.spHalf};
`;

interface Props {
  vendorId?: string;
  businessUnitId?: number;
  countryId?: number;
  warehouseId?: number;
  currencyIso: string;
  currencySymbol: string;
  existingSkus: string[];
  /** SKUs of entries removed in this session or marked REMOVED by an earlier apply —
   *  re-adding them via lookup is blocked; the row's Restore button brings them back */
  removedSkus: string[];
  onAddItem: (item: CatalogLookupResult) => void;
};

export const getBlockedSkuMessage = (sku: string, existingSkus: string[], removedSkus: string[]): string | null => {
  const normalizedSku = sku?.trim().toLowerCase();

  if (!normalizedSku) return null;

  const matches = (candidate: string) => candidate?.trim().toLowerCase() === normalizedSku;

  if (removedSkus.some(matches)) return TEXT_LABELS.ITEM_REMOVED_EARLIER;

  if (existingSkus.some(matches)) return TEXT_LABELS.ITEM_ALREADY_ADDED;

  return null;
};

/**
 * "Look up catalog item" section of the Edit order screen. Searches the vendor's catalog
 * for an exact SKU match (1-1 search, no smart search) and displays a single search result
 * with an Add action - the item is only added to the order once the user confirms via Add,
 * and only on the UI side, never persisted.
 */
const CatalogItemLookup = ({ vendorId, businessUnitId, countryId, warehouseId, currencyIso, currencySymbol, existingSkus, removedSkus, onAddItem }: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [fieldError, setFieldError] = useState(false);
  const [searchResult, setSearchResult] = useState<CatalogLookupResult | null>(null);

  const lookupReq = useAPI({
    deferFn: lookupCatalogItemBySku,
    onResolve: (result: CatalogLookupResponse) => {
      if (!result.found || !result.item) {
        setSearchResult(null);
        renderToast(toast.TYPE.ERROR, TEXT_LABELS.CATALOG_ITEM_NOT_FOUND);
        return;
      }

      setSearchResult(result.item);
    },
    onReject: () => renderToast(toast.TYPE.ERROR, TEXT_LABELS.CATALOG_ITEM_LOOKUP_FAILED),
    debugLabel: 'editOrderCatalogItemLookupRequest'
  });

  const handleSearchTermChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFieldError(false);
    setSearchTerm(e.target.value);
  };

  const runLookup = () => {
    const trimmedTerm = searchTerm.trim();

    if (!trimmedTerm) {
      setFieldError(true);
      return;
    }

    setFieldError(false);
    setSearchResult(null);

    const blockedMessage = getBlockedSkuMessage(trimmedTerm, existingSkus, removedSkus);
    if (blockedMessage) {
      renderToast(toast.TYPE.ERROR, blockedMessage);
      return;
    }

    lookupReq.run(trimmedTerm, vendorId, businessUnitId, countryId, warehouseId);
  };

  const handleAdd = () => {
    if (!searchResult) return;

    const blockedMessage = getBlockedSkuMessage(searchResult.sku, existingSkus, removedSkus);
    if (blockedMessage) {
      renderToast(toast.TYPE.ERROR, blockedMessage);
      return;
    }

    onAddItem(searchResult);
    setSearchResult(null);
    setSearchTerm('');
  };

  const productImage = searchResult?.productImages && searchResult.productImages.length > 0
    ? process.env.REACT_APP_MEDIA_URL! + searchResult.productImages[0].path
    : '';

  const productDescription = useMemo(
    () => searchResult?.productDescription ? stripHtmlTags(searchResult.productDescription).trim() : '',
    [searchResult]
  );

  return (
    <SectionCard>
      <SectionTitle>{TEXT_LABELS.LOOKUP_CATALOG_ITEM}</SectionTitle>
      <FieldLabel className="base">
        {TEXT_LABELS.ITEM_ID_SKU}
        <Asterisk>*</Asterisk>
      </FieldLabel>
      <FlexboxContainer alignItems="center" gap={Space.sp2x} margin={`${Space.spBase} 0 ${Space.spBase}`} width="100%">
        <Input
          id="edit-order-item-lookup"
          value={searchTerm}
          placeholder=""
          width="100%"
          disabled={lookupReq.isLoading}
          isInvalid={fieldError}
          errorMessage={TEXT_LABELS.ITEM_ID_SKU_REQUIRED}
          onChange={handleSearchTermChange}
        />
        <Button
          isGhost
          theme="brandDark"
          color={Main.brandDark}
          borderColor={Main.brandDark}
          padding={`0 ${Space.sp3x}`}
          disabled={lookupReq.isLoading}
          onClick={runLookup}
        >
          {TEXT_LABELS.LOOKUP}
        </Button>
      </FlexboxContainer>
      <HelperText className="caption">{TEXT_LABELS.LOOKUP_HELPER_TEXT}</HelperText>
      {
        searchResult &&
        <SearchResultsWrapper>
          <SectionTitle>{`${TEXT_LABELS.SEARCH_RESULTS} (1)`}</SectionTitle>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <HeaderCell width="40%">{TEXT_LABELS.PRODUCT}</HeaderCell>
                  <HeaderCell width="22%">{TEXT_LABELS.ITEM_ID_SKU}</HeaderCell>
                  <HeaderCell width="20%">{`${TEXT_LABELS.UNIT_PRICE} (${currencyIso})`}</HeaderCell>
                  <HeaderCell width="18%" align="center">{TEXT_LABELS.ACTION}</HeaderCell>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <BodyCell>
                    <FlexboxContainer alignItems="center" gap={Space.spBasePlus}>
                      <ProductImage src={productImage} alt="" />
                      <FlexboxContainer flexDirection="column" gap={Space.spQuarter} minWidth="0">
                        <ProductName>{searchResult.productName}</ProductName>
                        {productDescription && <ProductDescription title={productDescription}>{productDescription}</ProductDescription>}
                      </FlexboxContainer>
                    </FlexboxContainer>
                  </BodyCell>
                  <BodyCell>
                    <SkuText>{searchResult.materialNumber || searchResult.sku}</SkuText>
                  </BodyCell>
                  <BodyCell>{`${currencySymbol}${(searchResult.basePrice || 0).toFixed(2)}`}</BodyCell>
                  <BodyCell align="center">
                    <ActionCellContent>
                      <Button
                        isGhost
                        theme="brandDark"
                        color={Main.brandDark}
                        borderColor={Main.brandDark}
                        leftIcon={(size: string) => <Icon name="plus" size={parseInt(size)} />}
                        onClick={handleAdd}
                      >
                        {TEXT_LABELS.ADD}
                      </Button>
                    </ActionCellContent>
                  </BodyCell>
                </tr>
              </tbody>
            </Table>
          </TableWrapper>
        </SearchResultsWrapper>
      }
    </SectionCard>
  );
};

export default CatalogItemLookup;
